import * as React from "react";
import {useEffect, useState} from "react";
import {
  Card, CardContent, CircularProgress, Container,
  Drawer,
  Grid, IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText, Tooltip,
  Checkbox, Typography, Chip, useTheme,
  DialogActions
} from "@mui/material";
import {IndentSpace, Link1, PlainText, Title5} from "../../../domains/common/components/Typography";
import DeleteIcon from '@mui/icons-material/Delete';
import {CheckBoxWithLabel, FormInput, FormInputDate} from "../../../domains/common/components/FormInput";
import FormSelect from "../../../domains/common/components/FormSelect";
import {
  documentTypes,
  errorsDescription,
  exceptionsTitle,
  infoTooltip,
  mappingTypeSubtype,
} from "../../../dictionaries";
import {DocumentTypeIcon, PdfGrayIcon} from "../../../domains/common/components/SvgIcons";
import Box from "@mui/material/Box";
import {useNotify} from "../../../domains/common/components/AlertNotify";
import {
  deleteDocumentFromDeal,
  generateBankDruzhbaNotify,
  generateBankNoospheraNotify,
  generateNonMarriageStatement, generateReceiptSendForStateReg,
  rendXmlStatements, rmSubjectsFromDocumentsRef, setDocRefToSubject,
  updateDocuments,
  uploadNewDocument, uploadSeveralDocuments,
  uploadSigDocument,
  validateSigDocument
} from "../documents";
import {
  deleteDocRefFromSubject, getSubjectsFIOList,
  saveParticipantDocumentRef
} from "../participant";
import {getBackendURL, waitInputComplete} from "../../../fn";
import {RolesEnums} from "../../../enums";
import {Download} from "@mui/icons-material";
import {ErrorTooltip, InfoTooltip, TooltipInfoStyled} from "../../../domains/common/components/Tooltip";
import {
  hasDocumentRefToParticipant
} from "../../../validation";
import {ButtonDownload, ButtonUpload, ButtonGroupCustom, ButtonBase} from "../../../domains/common/components/Button";
import {DealActionsEnums, handleDealAction} from "../../../actions";
import {dealData} from "../deal";
import {hasStatus} from "../status";


export let documentsErrors = {
  refTo: false,
  hasErrors: () => Object.values(documentsErrors).includes(true)
}


/**
 *
 * @param document
 * @param name
 */
export const validateDocumentData = (document, name) => {
  if (!document)
    return documentsErrors;

  if (!name || (name && name === 'refTo'))
    documentsErrors.refTo = !hasDocumentRefToParticipant(document)

  return documentsErrors;
}

/**
 * @typedef FileValues
 * @type {Object}
 * @property {string} fileName
 * @property {string} fileDesc
 * @property {string} link
 * @property {string} type
 * @property {number} id
 */
export function FilesListContainer() {
  let [documents, setDocuments] = useState(dealData.documents);
  const [fileDrawer, openDrawer, closeDrawer] = FileDrawer({closeOnBackdrop: true});
  const [documentDrawerState, setDocumentDrawerState] = useState(false);
  const [drawerDocument, setDrawerDocument] = useState('');
  const [templateGenerationState, setTemplateGenerationState] = useState(null);
  const [open, setOpen] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [isDragOver,setIsDragOver] = useState(false);
  const [hasChange, setHasChange] = useState(false);
  const anchorRef = React.useRef(null);
  const buttonMenuOptions = [
    {key: 'bankDruzhbaNotify', value: 'Уведомление банка Дружба'},
    {key: 'bankNoospheraNotify', value: 'Уведомление банка Ноосфера'},
    {key: 'nonMortgageStatement', value: 'Заявление не в браке'},
    {key: 'rendXmlStatement', value: 'Визуализация XML-заявлений'},
    {key: 'receiptSendToStateRegistration', value: 'Расписка отправки документов'}
  ];
  let [title, setTitle] = useState('Документ');

  const [showProgress, setShowProgressState] = useState(false);
  let progressLine;

  const {openNotify} = useNotify();

  const theme = useTheme();

  if (showProgress) {
    progressLine = <LinearProgress/>
  }

  // обработка нажатия на Добавить
  const handleAddButton = () => {
    if (hasStatus('dealSentToRR')) {
      openNotify({msg: exceptionsTitle.DEAL_ALREADY_SENTTO_RR, duration: 3000, type: 'error'})
      return;
    }

    selectedDocument({})
  }

  const handleUploadSeveralFiles = async (files) => {
    setShowProgressState(true)

    if(dealData.configs.maxUploadFiles < files.length) {
      openNotify({msg: `Превышено максимальное количество файлов. Допустимо не более ${dealData.configs.maxUploadFiles} файлов за одну загрузку.`, duration: 3000, type: 'error'})
      setShowProgressState(false);
      return;
    }

    const result = await uploadSeveralDocuments(files);

    if (result?.ok) {
      const { documents = [], errors = [] } = result.data || {};

      // Обновляем документы
      if (documents.length > 0) {
        documents.forEach(file => dealData.documents.push(file));
        setDocuments([...dealData.documents]);
      }

      // Показываем уведомление
      const hasErrors = errors.length > 0;
      const msg = hasErrors
        ? `Загружено ${documents.length} из ${files.length} файлов${documents.length < files.length ? '. Есть ошибки:' : ''}`
        : `Успешно загружено ${documents.length} файла(ов)`;

      openNotify({
        msg,
        duration: hasErrors ? 6000 : 3000,
        errors: hasErrors ? errors : undefined,
        type: hasErrors ? (documents.length > 0 ? 'warning' : 'error') : 'success',
        positionH: 'right',
      });
    } else {
      openNotify({
        msg: result?.message || "При загрузке возникла ошибка",
        duration: 5000,
        errors: result?.errors,
        type: 'error',
        positionH: 'right',
      });
    }

    setShowProgressState(false);
  }

  // перетаскивание файлов с рабочего стола
  useEffect(() => {
    let dragCounter = 0;

    const onDragEnter = (e) => {
      e.preventDefault();
      dragCounter++;
      if (dragCounter === 1) {
        setIsDragOver(true);
      }
    };

    const onDragLeave = (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter === 0) {
        setIsDragOver(false);
      }
    };

    const onDragOver = (e) => {
      e.preventDefault();
      // Не меняем состояние здесь, только предотвращаем дефолтное поведение
    };

    const onDrop = (e) => {
      e.preventDefault();
      dragCounter = 0;
      setIsDragOver(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleUploadSeveralFiles(files);
      }
    };

    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);

    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
    };
  }, []);

  // Добавить дополнительные файлы
  const handleAddMenuItemClick = async (type) => {
    const banksNotifyActions = {
      bankDruzhbaNotify: {
        title: 'Создание Уведомления банка Дружба',
        action: generateBankDruzhbaNotify,
        errorMsg: 'Не удалось создать уведомление'
      },
      bankNoospheraNotify: {
        title: 'Создание Уведомления банка Ноосфера',
        action: generateBankNoospheraNotify,
        errorMsg: 'Не удалось создать уведомление'
      }
    };

    const configBanksNotifyAction = banksNotifyActions[type];

    if (configBanksNotifyAction) {
      setDisableButton(true);
      setTemplateGenerationState(<IconOnRendProgress title={configBanksNotifyAction.title} />);

      const result = await configBanksNotifyAction.action();

      if (result.ok) {
        if (Array.isArray(result.data)) {
          result.data.forEach((item) => dealData.documents.push(item))
        }
        setDocuments(dealData.documents);
      } else {
        openNotify({
          errors: result.errors,
          msg: exceptionsTitle[result.message] || configBanksNotifyAction.errorMsg,
          duration: 5000,
          type: 'error'
        })
      }
      setTemplateGenerationState(<></>)
      setDisableButton(false);
    }

    if(type === 'receiptSendToStateRegistration') {
      setDisableButton(true);
      setTemplateGenerationState(<IconOnRendProgress title="Создание расписки" /> );

      const result = await generateReceiptSendForStateReg();

      if (result.ok) {
        dealData.documents.push(result.data);
        setDocuments(dealData.documents);
      } else {
        openNotify({
          errors: result.errors,
          msg: exceptionsTitle[result.message] || 'Не удалось создать расписку',
          duration: 5000,
          type: 'error'
        })
      }
      setTemplateGenerationState(<></>)
      setDisableButton(false);
    }

    if (type === 'nonMortgageStatement') {
      const content = <NonMarriageListParticipants onSelect={async (participant) => {
        setDisableButton(true);
        setTemplateGenerationState(<IconOnRendProgress title="Создание заявления не в браке" />);

        const result = await generateNonMarriageStatement(participant.participantId);

        if (result.ok) {
          dealData.documents.push(result.data);

          // при генерации документ прикреплен к заявителю, прикрепляем на фронте без доп запросов в бек
          const p = dealData.participants.find((item) => item.participantId === participant.participantId)
          p.documentsRef?.push(result.data.id);

          if (!dealData.documentsRefListToParticipants[result.data.id]) {
            dealData.documentsRefListToParticipants[result.data.id] = [participant.participantId]
          } else {
            dealData.documentsRefListToParticipants[result.data.id].push(participant.participantId)
          }

          setDocuments(dealData.documents);
        } else {
          openNotify({
            errors: result.errors,
            msg: exceptionsTitle[result.message] || 'Не удалось создать заявление',
            duration: 5000,
            type: 'error'
          })
        }
        closeDrawer()
        setTemplateGenerationState(<></>)
        setDisableButton(false);
      }}/>

      openDrawer({content})
    }

    if (type === 'rendXmlStatement') {
      setDisableButton(true);
      setTemplateGenerationState(
        <>
          <Grid item xs={1}><PdfGrayIcon size="32"/></Grid>
          <Grid item xs={10}
                style={{verticalAlign: 'bottom', lineHeight: '38px', color: '#666'}}>Создание PDF-заявлений
            <IndentSpace size={5}/> <CircularProgress size="14px"/></Grid>
        </>);

      const result = await rendXmlStatements();

      if (result.ok) {
        if (result.data.documents?.length) {
          dealData.documents.push(...result.data.documents);
        }

        if (result.data.participants?.length) {

          const {participants} = dealData;
          const {documentsRefListToParticipants} = dealData;

          result.data.participants.forEach((participant) => {
            const {participantId, documentId} = participant;

            // Находим участника
            const participantObj = participants.find(p => p.participantId === participantId);
            participantObj?.documentsRef?.push(documentId.documentId);

            // Обновляем связи документов с участниками
            if (!documentsRefListToParticipants[documentId]) {
              documentsRefListToParticipants[documentId] = [participantId];
            } else {
              documentsRefListToParticipants[documentId].push(participantId);
            }
          })
        }

        if (result.data.deletedDocumentsIds?.length) {
          const deletedIdsSet = new Set(result.data.deletedDocumentsIds);

          dealData.documents = dealData.documents.filter(
            item => !deletedIdsSet.has(item.id)
          );
        }

        setDocuments(dealData.documents);
      } else {
        openNotify({
          errors: result.errors,
          msg: exceptionsTitle[result.message] || 'Неизвестная ошибка при создании заявления',
          duration: 5000,
          type: 'error'
        })
      }
      setTemplateGenerationState(<></>)
      setDisableButton(false);
    }

    if (anchorRef.current)
      return;
  };

  const deleteDocument = async (id) => {
    if (hasStatus('dealSentToRR')) {
      openNotify({msg: exceptionsTitle.DEAL_ALREADY_SENTTO_RR, duration: 3000, type: 'error'})
      return;
    }

    if (!id) {
      setDocuments([...dealData.documents]);
      return;
    }

    const result = await deleteDocumentFromDeal(id);

    if (result.ok)
      setDocuments([...dealData.documents]);
    else
      openNotify({
        msg: exceptionsTitle[result?.message] || result?.message || exceptionsTitle.DELETE_DOCUMENT_ERROR,
        duration: 5000,
        type: 'error'
      })
  }

  const selectedDocument = async (document) => {
    setDocumentDrawerState(true);
    setDrawerDocument(document)
  }

  const handleUploadResult = async ({formData}) => {
    if (hasStatus('dealSentToRR')) {
      openNotify({msg: exceptionsTitle.DEAL_ALREADY_SENTTO_RR, duration: 3000, type: 'error'})
      return;
    }
    selectedDocument({})
    setShowProgressState(true);
    setTitle('Идет загрузка')
    const result = await uploadNewDocument(formData);

    if(result?.ok) {
      dealData.documents.push(result.data)
      setDrawerDocument(result.data);
      setDocuments(dealData.documents);
    } else {
      openNotify({
        msg: exceptionsTitle[result.message] || result.message || "При загрузке возникла ошибка",
        duration: 10000,
        errors: result.errors,
        type: 'error',
        positionH: 'right',
      });
      setTitle('При загрузке возникла ошибка');
    }

    setShowProgressState(false);
  }

  const onChangeDesc = (e) => {
    setTitle(e.target.value)
  }

  const onChange = (e) => {
    setHasChange(true);
  }

  if (drawerDocument?.fileDesc)
    title = drawerDocument.fileDesc;

  return (
    <div>
      {fileDrawer}
      <Drawer open={documentDrawerState} anchor='right' ModalProps={{
        onBackdropClick: async () => {

          if (!hasChange) {
            setDocumentDrawerState(false);

            return;
          }

          const result = await updateDocuments(drawerDocument);
          if (result?.ok) {
            openNotify({msg: "Данные успешно сохранены", duration: 3000, type: 'success'})
          } else {
            openNotify({
              msg: exceptionsTitle[result.message] || result.message || "При сохранении возникла ошибка",
              duration: 5000,
              errors: result.errors,
              type: 'error',
              positionH: 'right',
            })
          }
          handleDealAction(DealActionsEnums.UPDATE_DOCUMENT)
          setDocuments(dealData.documents);
          setHasChange(false);
          setDocumentDrawerState(false);
        }
      }}>
        <Container sx={{width: 600, padding: 1}}>
          <>
            {progressLine}
            <Title5 title={title}/>
            {
              drawerDocument.id ?
                <FileFormComponent data={drawerDocument} onChangeDesc={onChangeDesc}
                                   onChange={() => onChange ? onChange() : void 0}/> : <ButtonUpload onSubmit={handleUploadResult}/>
            }
          </>
        </Container>
      </Drawer>
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <Title5 title="Документы"/>
        </Grid>
        <Grid item xs={3}>
          <Box display="flex" justifyContent="flex-end">
            <ButtonGroupCustom onButtonClick={handleAddButton} onMenuClick={handleAddMenuItemClick} disabled={disableButton} options={buttonMenuOptions}/>
          </Box>
        </Grid>
        <Grid item xs={12}>{showProgress ? <LinearProgress/> : null}</Grid>
        <Grid item container xs={12} spacing={0}>
          {documents.map((document) => {
              const errors = validateDocumentData(document)
              const pT = dealData.generatedDocuments.includes(document.id) ? '3px' : '8px'
              return (
                <>
                  <Grid item xs={11} sx={{height: 50}}>
                    <Grid container spacing={0}>
                      <Grid item xs={1}>
                        {errors.hasErrors() ?
                          <>
                            <IndentSpace size={5}/>
                            <ErrorTooltip sx={{paddingTop: '10px'}} placement="bottom"
                                          title={errorsDescription.DOCUMENT_NO_REF_TO_PARTICIPANT}/>
                          </> : <DocumentTypeIcon size="32" fileName={document.fileName}/>
                        }
                      </Grid>
                      <Grid item xs={11} style={{cursor: 'pointer', verticalAlign: 'bottom', paddingTop: pT}}
                            onClick={() => selectedDocument(document)}>
                        <PlainText>
                          {!document?.fileDesc ? 'Документ' : void 0}
                          {document.fileDesc?.slice(0, 50)}
                          {document.fileDesc?.length > 50 ? '...' : void 0}
                          <br/>
                          {dealData.generatedDocuments.includes(document.id) ? <Chip color="info" size="small" sx={{
                            fontSize: '9px',
                            backgroundColor: '#fdef9f',
                            color: '#000',
                            height: '14px',
                            marginTop: '-16px'
                          }} label="Сгенерирован автоматически"/> : ''}
                        </PlainText>
                      </Grid>
                    </Grid>

                  </Grid>
                  <Grid item xs={1} sx={{verticalAlign: 'top'}}>
                    <IconButton size="small" sx={{padding: 0, color: '#8d8d8d'}}
                                onClick={async () => {
                                  deleteDocument(document.id)
                                }}>
                      <DeleteIcon fontSize="inherit"/>
                    </IconButton>
                  </Grid>
                </>
              )
            }
          )}
          {templateGenerationState || void 0}
        </Grid>
      </Grid>
      {isDragOver && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            backgroundColor: theme.palette.primary.main,
            opacity:0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            border: `3px dashed ${theme.palette.background.paper}`,
          }}
        >
          <Box
            sx={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <Typography variant="h4" color='white'>
              Перетащите файлы для загрузки
            </Typography>
          </Box>
        </Box>
      )}
    </div>
  )
}

/**
 *
 * @param {DealDocument} data
 * @param onChangeDesc
 * @param onChange
 * @param onChangeDesc
 * @param onChange
 * @returns {Element}
 * @constructor
 */
export function FileFormComponent({data, onChangeDesc, onChange}) {
  const [value, setvalue] = useState(data);
  const {openNotify} = useNotify();
  const [loadingIcon, setLoadingIcon] = useState(false)
  const [validateSignatureLoader, setValidateSignatureLoader] = useState(false)
  const [validateSignatureFail, setValidateSignatureFail] = useState(false)
  const [signInfo, setSignInfo] = useState([])

  const theme = useTheme();

  const setDocumentValues = (val) => {
    onChange ? onChange() : void 0;

    dealData.documents.find((item) => {
      if (item.id === data.id) {
        Object.assign(item, {...item, ...val});
      }
    })
  }

  const changeDesc = (event) => {
    onChangeDesc ? onChangeDesc(event) : void 0;

    setDocumentValues({fileDesc: event.target.value});
  }

  const handleChangeSubtype = (event) => {
    const subtypeValue = event.target.value;
    let docType;

    if (subtypeValue) {
      docType = mappingTypeSubtype[subtypeValue];
    } else {
      docType = null
    }

    setDocumentValues({
      subtype: event.target.value,
      type: docType
    })
  }

  const handleUploadSigFile = async ({formData}) => {
    setLoadingIcon(true);

    if (hasStatus('dealSentToRR')) {
      openNotify({msg: exceptionsTitle.DEAL_ALREADY_SENTTO_RR, duration: 3000, type: 'error'})
      return;
    }

    const result = await uploadSigDocument(value.id, formData);

    if (result?.ok) {
      setDocumentValues({signlink: result.data.signlink});
      setvalue(Object.assign(value, {signlink: result.data.signlink}));
      setLoadingIcon(false);

      setValidateSignatureLoader(true)
      setSignInfo([])
      const validateResult = await validateSigDocument(dealData.deal.id, value.id);

      if (validateResult?.ok) {
        if (!validateResult.data?.length) {
          setValidateSignatureFail(true)
        } else {
          setSignInfo(validateResult.data)
        }
      }
      setValidateSignatureLoader(false)
    }
    setLoadingIcon(false);
  }

  const handleChange = (event) => {
    if (event.target?.type === 'checkbox') {
      setDocumentValues({[event.target.name]: event.target.checked});
    } else {
      setDocumentValues({[event.target.name]: event.target.value});
    }
  }

  useEffect(() => {
    const signInfosFromDealData = dealData.signatureInfo.filter((item) => item.documentId === value.id);
    setSignInfo(signInfosFromDealData)
  }, []);

  return (
    <div>
      <Grid container xs={12} spacing={1}>
        <Grid item xs={10.2}/>
        <Grid item xs={12} sx={{display: 'flex'}}>
          <TooltipInfoStyled placement={'bottom'} title="Открыть">
            <Link1 target="_blank" href={value.link}>{value.fileName}</Link1>
          </TooltipInfoStyled>
          <IndentSpace size={10}/>
          <TooltipInfoStyled placement={'bottom'} title="Скачать">
            <Link1 sx={{paddingTop: '3px', color: theme.palette.icon.default}} target="_blank" href={`${getBackendURL()}/documents/${value.id}/download`}>
              <Download fontSize="inherit"/>
            </Link1>
          </TooltipInfoStyled>
        </Grid>
        <Grid item xs={10.2}/>
        <Grid item xs={12}>
          <FormSelect data={documentTypes} label="Тип документа" minWidth="400" maxWidth="400"
                      name="subtype" value={value.subtype} onChange={handleChangeSubtype} maxContentLength="70"/></Grid>
        <Grid item xs={12}><FormInput label="Наименование документа" value={value.fileDesc}
                                      name="fileDesc" fullWidth
                                      onChange={waitInputComplete((e) => changeDesc(e), 700)}/></Grid>
        <Grid item xs={12}><FormInput label="Номер документа"
                                      value={value.number || 'б/н'} name="number"
                                      onChange={waitInputComplete((e) => handleChange(e), 700)}/></Grid>
        <Grid item xs={12}><FormInputDate label="Дата выдачи"
                                          name="issueDate"
                                          value={value.issueDate}
                                          onChange={(v) => setDocumentValues({"issueDate": v})}/>
        </Grid>
        <Grid item xs={12} sx={{height: '10px'}}/>
        <Grid item xs={12}>
          <SubjectsRefToDocumentNew documentId={value.id} values={data} onChange={onChange}/></Grid>
        {!value?.onlySelfSig ?
          <Grid item xs={8} sx={{display: 'flex'}}>
            <CheckBoxWithLabel label="Отправить документ на подпись" name="hasSignature"
                               checked={value.hasSignature}
                               onChange={handleChange}/>
            <InfoTooltip placement="bottom" title={infoTooltip.SEND_TO_SIGN}/>
          </Grid>
          : ''}
        {!value?.onlySelfSig ?
          <Grid item xs={12}>
            <InternalSigners
              value={value.internalSigner}
              onChange={(e) => setDocumentValues({"internalSigner": e.target.value})}/>
          </Grid>
          : ''}
        <Grid item xs={12} sx={{height: 10}}/>
        <Grid item xs={12}>
          <Title5 title="Подпись"/>
        </Grid>
        <Grid item xs={12}>
          <Tooltip placement="bottom-start" title={infoTooltip.UPLOAD_PRTICIPANT_SIGNATURE}>
            <span>
              <ButtonUpload onSubmit={handleUploadSigFile} title='Загрузить подпись'/>
            </span>
          </Tooltip>
          {loadingIcon ? <CircularProgress size="10px"/> : void 0}
          <IndentSpace size={10}/>
          {
            value.signlink ?
              <Tooltip placement="bottom-start" title={infoTooltip.DOWNLOAD_DOCUMENT_SIG}>
                <span>
                  <ButtonDownload title="Скачать подпись" href={value.signlink}/>
                </span>
              </Tooltip>
              : ''
          }
          <IndentSpace size={10}/>
          {
            value.facsimilelink ?
              <Tooltip placement="bottom-start"
                       title={infoTooltip.DOWNLOAD_DOCUMENT_WITH_FACSIMILIE}>
                <ButtonDownload href={`${getBackendURL()}/documents/${value.id}/download/facsimile`} title="Скачать с факсимиле"></ButtonDownload>
              </Tooltip>
              : void 0
          }
        </Grid>
        <Grid item xs={12} sx={{height: '10px'}}></Grid>
        <Grid item xs={12}>
          {
            validateSignatureLoader ? 'Идет проверка подписи ...' : ''
          }
          {
            validateSignatureFail ? 'Не удалось проверить подпись' : ''
          }
          {
            signInfo.length ? <SignInfoContainer values={signInfo}/> : void 0
          }
        </Grid>
        <Grid item xs={10.2}/>
      </Grid>
    </div>
  )
}

/**
 * Создание ссвязи между документом и заявителями
 * @param personId
 * @returns {Element}
 * @constructor
 */
export function SubjectsRefToDocumentNew({documentId, onChange, values}) {
  const [subjectFIOList, setSubjectFIOList] = useState(getSubjectsFIOList())
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [errors, setErrors] = useState(documentsErrors);

  useEffect(() => {
    dealData.documentsRefListToParticipants[documentId] ?
      setSelectedParticipants(dealData.documentsRefListToParticipants[documentId]) : void 0

    setErrors(validateDocumentData(values))
  }, []);

  const addDocument = (participantId) => {
    onChange ? onChange() : void 0;
    setDocRefToSubject({participantId, documentId});
    saveParticipantDocumentRef(participantId);
    setSelectedParticipants(dealData.documentsRefListToParticipants[documentId])
    setSubjectFIOList([...getSubjectsFIOList()]);
    setErrors(validateDocumentData(values))
  }

  const deleteDocument = (participantId) => {
    onChange ? onChange() : void 0;
    rmSubjectsFromDocumentsRef(documentId, participantId);
    deleteDocRefFromSubject({participantId, documentId});
    saveParticipantDocumentRef(participantId);
    setSelectedParticipants(dealData.documentsRefListToParticipants[documentId])
    setSubjectFIOList([...getSubjectsFIOList()]);
    setErrors(validateDocumentData(values))
  }

  return (
    <Grid item xs={10} container spacing={0}>
      <Grid item xs={12} sx={{display: 'flex'}}>
        <Title5 title="Прикрепить к заявителю"/>
        {errors.refTo ?
          <>
            <IndentSpace size={5}/>
            <ErrorTooltip sx={{paddingTop: '4px'}} placement="bottom"
                          title={errorsDescription.DOCUMENT_NO_REF_TO_PARTICIPANT}/>
          </>
          :
          <>
            <IndentSpace size={5}/>
            <InfoTooltip placement="bottom" sx={{paddingTop: '4px'}} title={infoTooltip.ATTACH_DOC_TO_PARTICIPANT}/>
          </>
        }
      </Grid>
      <Grid item xs={12}>
        {
          subjectFIOList.map((item) => {
              const selected = selectedParticipants?.includes(item.key);
              return (<List component="nav" aria-label="" sx={{paddingBottom: '1px', paddingTop: '0px'}}>
                <ListItem
                  button
                  sx={{cursor: 'pointer', height: '38px'}}
                  onClick={() => selected ? deleteDocument(item.key) : addDocument(item.key)}
                >
                  <Checkbox checked={selected}/>
                  <ListItemText secondary={
                    <PlainText>
                      {item.value}
                    </PlainText>
                  }/>
                </ListItem>
              </List>)
            }
          )
        }
      </Grid>
    </Grid>
  )
}

/**
 * Выбор внешнего подписанта
 * @returns {Element}
 * @constructor
 */
export function InternalSigners({onChange, value}) {
  return (
    <Grid item xs={10} container spacing={0}>
      <Grid item xs={12} sx={{display: 'flex'}}>
        <Title5 title="Подписать представителем банка"/>
        <IndentSpace size={5}/>
        {
          <InfoTooltip placement="bottom" sx={{paddingTop: '3px'}} title={infoTooltip.INNER_SIGNER}/>
        }
      </Grid>
      <Grid item xs={12} sx={{height: 10}}/>
      <Grid item xs={12}>
        <FormSelect
          data={dealData?.internalSigners}
          label="Выберете представителя"
          minWidth="400"
          disabled={!dealData?.internalSigners?.length}
          value={value || []}
          onChange={onChange || void 0}/>
      </Grid>
    </Grid>
  )
}

export function FileDrawer({closeOnBackdrop}) {
  const [drawerState, setDrawerState] = useState(false);
  const [content, setContent] = useState('');

  const openDrawer = ({content}) => {
    setContent(content);
    setDrawerState(true);
  };

  const closeDrawer = () => {
    setDrawerState(false);
  };
  return [
    (<Drawer open={drawerState} anchor='right' ModalProps={{
      onBackdropClick: async () => {
        if (closeOnBackdrop)
          setDrawerState(false);
      }
    }}>
      <Container sx={{width: 600, padding: 1}}>
        {content}
      </Container>
    </Drawer>),
    openDrawer,
    closeDrawer
  ]
}

/**
 * Дровер со списком участников
 * @constructor
 */
export function NonMarriageListParticipants({onSelect}) {
  const participants = dealData.participants.filter(item => item.role !== RolesEnums.REPRESENTATIVE)

  return (<Container sx={{width: 600, padding: 1}}>
    <Typography variant="h6" gutterBottom display="inline">Заявление не в браке. Выберете участника</Typography>
    <IndentSpace size={10}/>
    <Grid container spacing={0} alignItems="center" justifyContent="left">
      <Grid item xs={11} display="inline">

        {participants.map((item) => {
          if (item.type !== 'person')
            return;

          return <Grid container sx={[{cursor: 'pointer'}, {
            '&:hover': {backgroundColor: '#f0f0f0'},
          }]} onClick={() => {
            onSelect ? onSelect(item) : void 0;
          }}>
            <Grid item xs={12} sx={{height: 15}}></Grid>
            <Grid item xs={12}>
              <Grid container spacing={1} xs={12} sx={{pl: '15px'}}>
                <Grid item sx={{pr: 1}}>
                  <Typography variant="body2" component="div" display="inline">
                    {item?.lastName} {item?.firstName} {item?.middleName}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}></Grid>
            <Grid item xs={12} sx={{height: 15, borderBottom: 1, borderColor: '#dadada'}}></Grid>
          </Grid>
        })}

      </Grid>
    </Grid>
  </Container>)
}

/**
 * Выбор внешнего подписанта
 * @returns {Element}
 * @constructor
 */
export function SignInfoContainer({values}) {
  if (!values)
    return;

  return (
    <Grid item xs={10} container spacing={0}>
      {
        values.map((item) => {
          return (
            <>
              <Grid item xs={12} sx={{height: '10px'}}></Grid>
              {item.isValid ? <SignInfoComponent data={item}/> : <SignInfoNotValidComponent/>}
            </>)
        })
      }
    </Grid>
  )
}

/**
 *
 * @param data
 * @constructor
 */
export function SignInfoComponent({data}) {
  if (!data)
    return

  return (
    <Card sx={{maxWidth: 300, border: '1px solid #1b00ff'}}>
      <CardContent sx={{color: '#6d61ff', fontSize: '12px'}}>
        <span style={{fontWeight: 'bold'}}>Документ подписан усиленной квалифицированной подписью</span> <br/> <br/>
        Серийный номер: {data.serialNumber} <br/>
        Подписант: {data.signerName} <br/>
        Владелец ЦП: {data.issuer} <br/>
        Действительно с {data.signatureValidFrom} по {data.signatureValidTo} <br/>
        Дата подписания: {data.signingTime} <br/>
      </CardContent>
    </Card>
  )

}

/**
 *
 * @constructor
 */
export function SignInfoNotValidComponent() {
  return (
    <Card sx={{maxWidth: 300, border: '1px solid #ff0000'}}>
      <CardContent sx={{color: '#ff0000', fontSize: '13px'}}>
        Подпись недействительна
      </CardContent>
    </Card>
  )
}

export const IconOnRendProgress = ({title}) => {
  return (<>
    <Grid item xs={1}><PdfGrayIcon size="32"/></Grid>
    <Grid item xs={10}
          style={{verticalAlign: 'bottom', lineHeight: '38px', color: '#666'}}>{title}
      <IndentSpace size={5}/> <CircularProgress size="14px"/></Grid>
  </>)
}